import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, IconButton, Tooltip, Chip, Link, Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Refresh as RefreshIcon, Campaign as CampaignIcon, People as PeopleIcon,
  CheckCircle as CheckCircleIcon, Paid as PaidIcon, TrendingUp as TrendingUpIcon,
  Instagram as InstagramIcon, Visibility as VisibilityIcon, FavoriteBorder as LikeIcon,
  ChatBubbleOutline as CommentIcon, OpenInNew as OpenInNewIcon, Info as InfoIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

const money = (n: number | null | undefined): string =>
  (n === null || n === undefined) ? '—' : 'RD$' + Math.round(n).toLocaleString('es-DO');
const num = (n: number | null | undefined): string =>
  (n === null || n === undefined) ? '—' : Math.round(n).toLocaleString('es-DO');
// Las fechas llegan como 'YYYY-MM-DD'; new Date() sobre eso las lee en UTC y en RD se corren un día.
const dia = (s?: string): string => {
  if (!s) return '';
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-DO', { day: 'numeric', month: 'short' });
};
const horaRD = (iso?: string): string =>
  iso ? new Date(iso).toLocaleString('es-DO', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'America/Santo_Domingo' }) : '';

interface StatTileProps { icon: React.ReactNode; label: string; value: string; color?: string; md?: number; }
const StatTile: React.FC<StatTileProps> = ({ icon, label, value, color, md = 2.4 }) => (
  <Grid size={{ xs: 6, sm: 4, md }}>
    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', height: '100%' }}>
      <Box sx={{ color: color || 'primary.main', mb: 0.5 }}>{icon}</Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: color || 'text.primary' }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Paper>
  </Grid>
);

const SocialDashboardPage: React.FC = () => {
  const [summary, setSummary] = useState<any>(null);
  const [organic, setOrganic] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, o] = await Promise.all([
        apiService.getSocialSummary().catch(() => null),
        apiService.getSocialOrganic().catch(() => null),
      ]);
      setSummary(s?.data || null);
      setOrganic(o?.data || null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const adsOff = summary && summary.adSpendAvailable === false;
  const acc = organic?.account;
  const top: any[] = organic?.topMedia || [];
  const cierres: any[] = summary?.cierresDetalle || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <CampaignIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Redes Sociales</Typography>
        {summary?.rango && (
          <Chip
            size="small"
            label={`${dia(summary.rango.from)} – ${dia(summary.rango.to)}`}
            sx={{ ml: 1.5 }}
            title="Todo lo de abajo es de este período (el mes en curso)"
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refrescar">
          <IconButton onClick={fetchAll}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {adsOff && (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
              Conecta la cuenta de anuncios al usuario del sistema para ver gasto y ROI. Mientras tanto se muestran leads, cierres y categorización.
            </Alert>
          )}

          {/* KPIs del embudo */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <StatTile md={2} icon={<PaidIcon />} label="Gasto en ads" value={money(summary?.gasto)} />
            <StatTile md={2} icon={<PeopleIcon />} label="Leads" value={num(summary?.leads)} />
            <StatTile md={2} icon={<CheckCircleIcon />} label="Cierres de anuncio" value={num(summary?.cierres)} color="#2e7d32" />
            <StatTile md={2} icon={<PeopleIcon />} label="Clientes nuevos" value={num(summary?.clientesNuevos)} />
            <StatTile md={2} icon={<TrendingUpIcon />} label="Costo/cierre" value={money(summary?.costoPorCierre)} />
            <StatTile
              md={2} icon={<TrendingUpIcon />} label="Neto"
              value={money(summary?.neto)}
              color={(summary?.neto ?? 0) >= 0 ? '#2e7d32' : '#c62828'}
            />
          </Grid>

          {/* Quién cerró y de dónde salió. Es lo que hace comprobable el número de arriba: un
              cierre es un cliente dado de alta en el período cuya conversación de anuncio empezó
              ANTES del alta. Las altas sin anuncio se muestran igual, para que cuadre con lo que
              se cuenta a mano al final del día. */}
          {cierres.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Clientes nuevos del período ({cierres.length})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {num(summary?.cierres)} vinieron de un anuncio y {num(summary?.cierresSinAnuncio)} por otros medios.
                El cierre se cuenta el día del alta, aunque el lead sea de semanas antes.
              </Typography>
              <Box sx={{ mt: 1.5 }}>
                {cierres.map((c: any, i: number) => (
                  <Box key={`${c.tel}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>
                      {horaRD(c.alta)}
                    </Typography>
                    <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>{c.nombre}</Typography>
                    {c.adId ? (
                      <Chip size="small" color="success" variant="outlined" label={c.anuncio} sx={{ maxWidth: 260 }} />
                    ) : (
                      <Chip size="small" variant="outlined" label="Sin anuncio" />
                    )}
                    {c.diasDesdeElLead !== null && c.diasDesdeElLead !== undefined && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={c.diasDesdeElLead === 0 ? 'mismo día' : `${c.diasDesdeElLead} d`}
                        title="Días entre el primer mensaje del lead y el alta"
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          {/* Instagram orgánico */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <InstagramIcon sx={{ mr: 1, color: '#c13584' }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Instagram orgánico {acc?.username ? `(@${acc.username})` : ''}
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <StatTile icon={<PeopleIcon />} label="Seguidores" value={num(acc?.followers)} />
              <StatTile icon={<VisibilityIcon />} label="Alcance 30d" value={num(acc?.reach30d)} />
              <StatTile icon={<VisibilityIcon />} label="Visitas perfil 30d" value={num(acc?.profileViews30d)} />
              <StatTile icon={<InstagramIcon />} label="Publicaciones" value={num(acc?.mediaCount)} />
            </Grid>

            <Alert severity="warning" sx={{ mt: 2 }}>
              Ojo: el conteo de seguidores incluye muchos inactivos/extranjeros (comprados al inicio).
              Mide por <b>alcance + engagement + conversiones a WhatsApp</b>, no por el número de seguidores.
            </Alert>

            {top.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Top posts (por alcance)</Typography>
                {top.slice(0, 6).map((p) => (
                  <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap title={p.caption}>{p.caption || '(sin texto)'}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.25 }}>
                        <Chip size="small" icon={<VisibilityIcon />} label={num(p.reach)} variant="outlined" />
                        <Chip size="small" icon={<LikeIcon />} label={num(p.likes)} variant="outlined" />
                        <Chip size="small" icon={<CommentIcon />} label={num(p.comments)} variant="outlined" />
                        {p.type && <Chip size="small" label={p.type} />}
                      </Box>
                    </Box>
                    {p.permalink && (
                      <Tooltip title="Abrir en Instagram">
                        <IconButton size="small" component={Link} href={p.permalink} target="_blank" rel="noopener">
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default SocialDashboardPage;
